import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  CheckCircle2,
  FileSearch,
  MessageSquare,
  Shield,
  Zap,
  Building2,
  Clock,
  Plane,
  Mail,
} from 'lucide-react'

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900">
              <Plane className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-navy-900">Voltaire AI</span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#features" className="text-sm font-medium text-gray-600 hover:text-navy-900">
              Features
            </a>
            <a href="#how-it-works" className="text-sm font-medium text-gray-600 hover:text-navy-900">
              How It Works
            </a>
            <a href="#contact" className="text-sm font-medium text-gray-600 hover:text-navy-900">
              Contact
            </a>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" asChild>
              <Link to="/login">Sign In</Link>
            </Button>
            <Button asChild>
              <a href="#contact">Request Demo</a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden pt-16">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 lg:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-8">
            <div className="flex flex-col justify-center">
              <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white">
                <Zap className="h-4 w-4" />
                <span>Enterprise-grade aviation AI</span>
              </div>
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-white lg:text-5xl xl:text-6xl">
                AI-Powered Maintenance Documentation
              </h1>
              <p className="mb-8 text-lg text-gray-300 lg:text-xl">
                Get instant, accurate answers from your aviation maintenance manuals.
                Every response includes precise citations for full regulatory compliance.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button size="xl" className="bg-white text-navy-900 hover:bg-gray-100" asChild>
                  <a href="#contact">
                    Request Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="xl" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                  <Link to="/login">Sign In</Link>
                </Button>
              </div>

              {/* Trust badges */}
              <div className="mt-12 flex items-center gap-8">
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Shield className="h-5 w-5" />
                  <span>SOC 2 Compliant</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>FAA/EASA Ready</span>
                </div>
              </div>
            </div>

            {/* Hero Illustration */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-xl border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
                <div className="mb-4 flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-400" />
                  <div className="h-3 w-3 rounded-full bg-yellow-400" />
                  <div className="h-3 w-3 rounded-full bg-green-400" />
                </div>
                <div className="space-y-4">
                  <div className="rounded-lg bg-white/10 p-4">
                    <p className="text-sm text-gray-300">
                      What's the tire pressure for the B737-800 main landing gear?
                    </p>
                  </div>
                  <div className="rounded-lg bg-white p-4">
                    <p className="text-sm text-gray-800">
                      The main landing gear tire pressure for the B737-800 is <strong>205 PSI</strong> at ambient temperature.
                    </p>
                    <div className="mt-3 rounded bg-blue-50 p-2 text-xs text-blue-700">
                      📄 AMM 32-11-01, Rev 45, Pages 201-203
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -right-4 -top-4 rounded-lg bg-white p-3 shadow-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Citation verified</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" className="w-full">
            <path d="M0 100V0C240 66.667 480 100 720 100C960 100 1200 66.667 1440 0V100H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-navy-900 lg:text-4xl">
              Built for Aviation Professionals
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Voltaire AI understands aviation maintenance documentation like no other system.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={MessageSquare}
              title="Instant Answers"
              description="Ask questions in plain English and get accurate answers from your maintenance manuals in seconds, not hours."
            />
            <FeatureCard
              icon={FileSearch}
              title="Precise Citations"
              description="Every answer includes exact document references — manual, revision, section, and page numbers for full traceability."
            />
            <FeatureCard
              icon={Shield}
              title="Compliance Ready"
              description="Built-in audit trails meet FAA Part 145 and EASA requirements. Every query is logged with tamper-proof signatures."
            />
            <FeatureCard
              icon={Clock}
              title="24/7 Availability"
              description="Your maintenance knowledge base is always available, reducing dependency on senior technicians."
            />
            <FeatureCard
              icon={Building2}
              title="Works With Your DMS"
              description="Integrates with your existing document management system — no need to migrate your manuals."
            />
            <FeatureCard
              icon={Zap}
              title="Continuous Updates"
              description="New manual revisions are automatically indexed. Always query the latest approved documentation."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-navy-900 lg:text-4xl">
              How It Works
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Get started in minutes, not months.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            <StepCard
              number="01"
              title="Upload Your Manuals"
              description="Securely upload your AMMs, CMMs, SBs, and other maintenance documentation. We support all major formats."
            />
            <StepCard
              number="02"
              title="AI Indexes Content"
              description="Our aviation-trained AI processes and indexes your manuals, understanding structure, sections, and relationships."
            />
            <StepCard
              number="03"
              title="Ask Questions"
              description="Your team can immediately start asking questions and getting cited answers from your documentation."
            />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="text-center">
            <h2 className="mb-4 text-3xl font-bold text-navy-900 lg:text-4xl">
              Trusted by Aviation Maintenance Professionals
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Built specifically for MRO operations, with a focus on accuracy, compliance, and ease of use.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="hero-gradient py-24">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <h2 className="mb-4 text-3xl font-bold text-white lg:text-4xl">
            Ready to Transform Your Maintenance Operations?
          </h2>
          <p className="mb-8 text-lg text-gray-300">
            Get in touch to learn how Voltaire AI can help your team access maintenance
            information faster while maintaining full regulatory compliance.
          </p>
          <div className="mx-auto max-w-md rounded-xl bg-white/10 p-6 backdrop-blur-sm">
            <div className="flex items-center justify-center gap-3 text-white">
              <Mail className="h-6 w-6" />
              <a href="mailto:info@voltaireai.co" className="text-lg font-medium hover:underline">
                info@voltaireai.co
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-300">
              Contact us for a personalized demo and pricing information.
            </p>
          </div>
          <div className="mt-8">
            <Button size="xl" className="bg-white text-navy-900 hover:bg-gray-100" asChild>
              <Link to="/login">
                Sign In to Your Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <Link to="/" className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900">
                  <Plane className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-semibold text-navy-900">Voltaire AI</span>
              </Link>
              <p className="mt-4 text-sm text-gray-600">
                AI-powered aviation maintenance documentation for the modern MRO.
              </p>
            </div>
            <div>
              <h4 className="mb-4 font-semibold text-navy-900">Product</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#features" className="hover:text-navy-900">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-navy-900">How It Works</a></li>
                <li><a href="#contact" className="hover:text-navy-900">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-semibold text-navy-900">Company</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-navy-900">About</a></li>
                <li><a href="#" className="hover:text-navy-900">Careers</a></li>
                <li><a href="#" className="hover:text-navy-900">Contact</a></li>
                <li><a href="#" className="hover:text-navy-900">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-semibold text-navy-900">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-navy-900">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-navy-900">Terms of Service</a></li>
                <li><a href="#" className="hover:text-navy-900">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t pt-8 text-center text-sm text-gray-600">
            <p>&copy; {new Date().getFullYear()} Voltaire AI Solutions LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-navy-900/10">
        <Icon className="h-6 w-6 text-navy-900" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-navy-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

function StepCard({
  number,
  title,
  description,
}: {
  number: string
  title: string
  description: string
}) {
  return (
    <div className="relative rounded-xl border bg-white p-6 shadow-sm">
      <div className="mb-4 text-4xl font-bold text-navy-900/10">{number}</div>
      <h3 className="mb-2 text-lg font-semibold text-navy-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

